import _ from 'underscore';
import { format, parseISO, subDays } from 'date-fns';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Animation from 'components/Animation';
import ButtonFilter from 'components/ButtonFilter';
import DatePicker from 'components/DatePicker';
import PositionedBlock from 'components/PositionedBlock';
import ShowOnlyClient from 'components/ShowOnlyClient';

const Calendar = ({ name, dates, values, onChange }) => {
	const calendarButtonRef = useRef(null);
	const [showDatePicker, setShowDatePicker] = useState(false);

	const closeDatePicker = useCallback(() => {
		setShowDatePicker(false);
	}, []);

	const onDayApply = (currentDates) => {
		const newDates = { ...currentDates };

		if (currentDates.startDate && !currentDates.endDate) {
			newDates.endDate = currentDates.startDate;
		}

		onChange(name, newDates);

		setShowDatePicker(false);
	};

	const onCalendarClick = useCallback(() => {
		setShowDatePicker(!showDatePicker);
	}, [showDatePicker]);

	const getDateLabel = useCallback(() => {
		const { startDate, endDate } = dates;

		if (startDate) {
			let changedDates = [startDate];

			if (endDate && startDate !== endDate) {
				changedDates = [startDate, endDate];
			}

			return _(changedDates)
				.map((date) => format(parseISO(date), 'dd.MM.yyyy'))
				.join('-');
		}

		return '';
	}, [dates]);

	const dateLabel = useMemo(() => (!_.isEmpty(dates) ? getDateLabel() : ''), [
		dates
	]);

	return (
		<>
			<ShowOnlyClient>
				<ButtonFilter
					icon="date"
					text={dateLabel}
					onClick={onCalendarClick}
					active
					data-catalog="calendar"
					size="mobileSmall"
					ref={calendarButtonRef}
				/>
			</ShowOnlyClient>
			{calendarButtonRef.current && (
				<Animation show={showDatePicker}>
					<PositionedBlock
						anchorRef={calendarButtonRef}
						onRequestClose={closeDatePicker}
						offset={25}
					>
						<DatePicker
							onDayApply={onDayApply}
							availableStartDate={subDays(new Date(), 1)}
							availableDates={values}
							startDate={dates.startDate && parseISO(dates.startDate)}
							endDate={dates.endDate && parseISO(dates.endDate)}
						/>
					</PositionedBlock>
				</Animation>
			)}
		</>
	);
};

Calendar.propTypes = {
	name: PropTypes.string.isRequired,
	onChange: PropTypes.func.isRequired,
	dates: PropTypes.shape({
		startDate: PropTypes.string,
		endDate: PropTypes.string
	}),
	values: PropTypes.arrayOf(PropTypes.string)
};

Calendar.defaultProps = {
	dates: {},
	values: []
};

export default Calendar;
